import type { FormEvent, JSX, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface FormDialogProps {
  title: string;
  description?: ReactNode;
  submitLabel: string;
  destructive?: boolean;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
  children?: ReactNode;
}

// Every create, edit and confirm in this page is one of these, mounted only while it is open so its state starts fresh
const FormDialog = ({
  title,
  description,
  submitLabel,
  destructive = false,
  isSaving,
  error,
  onClose,
  onSubmit,
  children,
}: FormDialogProps): JSX.Element => {
  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Dialog open onOpenChange={(isOpen) => (!isOpen && !isSaving ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription asChild>
                <div className="text-sm text-text-muted">{description}</div>
              </DialogDescription>
            ) : null}
          </DialogHeader>
          {children ? <div className="flex flex-col gap-4">{children}</div> : null}
          {error ? (
            <div className="rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error whitespace-pre-wrap">
              {error}
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" variant={destructive ? 'destructive' : 'default'} disabled={isSaving}>
              {isSaving ? 'Guardando…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: ReactNode;
  children: ReactNode;
}

export const Field = ({ label, htmlFor, hint, children }: FieldProps): JSX.Element => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
    {hint ? <p className="m-0 text-xs text-text-muted">{hint}</p> : null}
  </div>
);

export default FormDialog;
